import React, { forwardRef } from 'react';
import ReactSVG from 'react-svg';
import { useTheme } from 'styled-components';
import { IconWrapper } from './styles';
import { ICON_RENDERERS } from './constants';


/**
 * Standard icon component w controlled color for v2 icons
 * - `type` should be a file prefix from `assets/v2-icons/`
 */
const Icon = forwardRef(({
  type,
  size = 'md',
  selected = false,
  disabled = false,
  color = undefined,
  hoverColor = undefined,
  inheritColor = false,
  label = undefined,
  className = '',
  style: styleProp = {},
  onClick = undefined,
  children = null,
  ...rest
}, ref) => {
  const theme = useTheme();

  const resolvedSize = typeof size === 'number'
    ? `${size}px`
    : (theme.iconSize[size] ?? theme.iconSize.md);

  const resolvedColor = color
    ? color
    : disabled
      ? theme.grey3
      : inheritColor
        ? undefined
        : type === 'close'
          ? theme.textLight
          : theme.textMedium;

  const wrapperStyle = {
    ...(resolvedColor !== undefined && { color: resolvedColor }),
    opacity: disabled && !color ? 0.45 : 1,
    ...styleProp,
  };

  const a11yProps = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': true };

  const CustomRenderer = ICON_RENDERERS[type];

  return (
    <IconWrapper
      ref={ref}
      $width={resolvedSize}
      $height={resolvedSize}
      $hoverColor={disabled ? undefined : hoverColor}
      data-icon={type}
      className={`icon ${className}`}
      style={wrapperStyle}
      onClick={onClick}
      {...a11yProps}
      {...rest}
    >
      {children ?? (
        CustomRenderer
          ? <CustomRenderer selected={selected} />
          : <ReactSVG
            src={require(`../../assets/v2-icons/${type}.svg`)}
            onError={(err) => console.error(`Failed to load icon: ${type}`, err)}
          />
      )}
    </IconWrapper>
  );
});

/**
 * Re-export with explicit prop typing so consumers in TS files
 * see the correct props through `forwardRef`.
 *
 * @typedef {Object} IconProps
 * @property {string} type
 * @property {string | number} [size]
 * @property {boolean} [selected]
 * @property {boolean} [disabled]
 * @property {string} [color]
 * @property {string} [hoverColor]
 * @property {boolean} [inheritColor]
 * @property {string} [label]
 * @property {string} [className]
 * @property {React.CSSProperties} [style]
 * @property {(e: React.MouseEvent) => void} [onClick]
 * @property {React.ReactNode} [children]
 */

/** @type {React.ForwardRefExoticComponent<IconProps & React.RefAttributes<HTMLDivElement>>} */
const TypedIcon = Icon;

export default TypedIcon;